/**
 * Main App Component
 *
 * Bootstraps the TanStack Router with QueryClient and auth context.
 * The full route tree is defined in router.tsx.
 */

import { RouterProvider } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { useAdminAuth } from './hooks/useAdminAuth';
import { useDarkMode } from './hooks/useDarkMode';
import { router } from './router';
import { setRouterRef } from './lib/routerRef';
import { createQueryClient } from './lib/queryClient';
import ErrorBoundary from './components/shared/ErrorBoundary';

// Configure React Query (no retries here — the admin client retries reads)
const queryClient = createQueryClient();

// Register the router reference for use by API interceptors
setRouterRef(router);

// Inner component — needs to be inside AdminAuthProvider to call useAdminAuth
const InnerApp = () => {
  const auth = useAdminAuth();
  // Apply persisted theme + keep system-preference listener attached on every
  // route, including public ones that never render AdminHeader.
  useDarkMode();

  return <RouterProvider router={router} context={{ queryClient, auth }} />;
};

const App = () => {
  // Top-level error boundary wraps EVERY provider, so an exception thrown by
  // AdminAuthProvider, the QueryClient, the router, or any descendant gets
  // caught and surfaced via the existing Card-based fallback UI instead of
  // dropping the user on a blank page. The TanStack Router's
  // `defaultErrorComponent` only catches errors inside the routed tree —
  // anything thrown by a provider above the RouterProvider escapes it.
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AdminAuthProvider>
            <InnerApp />
          </AdminAuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
